import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Snackbar,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditIcon from "@mui/icons-material/Edit";
import axios from "axios";

const emptyForm = {
  categoryCode: "",
  productSubCode: "",
  messageText: "",
};

const normalizeSubCode = (subCode) => {
  if (subCode === null || subCode === undefined || subCode === "") {
    return "98";
  }

  return String(subCode);
};

const sortSubCodeOptions = (items) =>
  [...items].sort((a, b) => {
    const aIs98 = String(a.subCode) === "98";
    const bIs98 = String(b.subCode) === "98";
    if (aIs98 !== bIs98) {
      return aIs98 ? 1 : -1;
    }

    return String(a.subCode).localeCompare(String(b.subCode), undefined, {
      numeric: true,
      sensitivity: "base",
    });
  });

const buildProductSubCodeOptions = (productData, countryId, categoryCode) => {
  if (!countryId || !categoryCode) {
    return [];
  }

  let subCodeRows = productData
    .filter(
      (item) =>
        item.countryCode === countryId &&
        String(item.categoryCode) === String(categoryCode)
    )
    .map((item) => ({
      subCode: String(item.subCode ?? ""),
      label: item.productLongDescription || item.productShortDescription || "",
    }));

  const hasEmptySubCode = subCodeRows.some((item) => item.subCode === "");

  if (subCodeRows.length === 0 || (subCodeRows.length === 1 && hasEmptySubCode)) {
    return [{ subCode: "98", label: "98. N/A" }];
  }

  if (hasEmptySubCode) {
    subCodeRows = subCodeRows.filter((item) => item.subCode !== "");
    subCodeRows.push({ subCode: "98", label: "98. N/A" });
  }

  const uniqueBySubCode = new Map();
  subCodeRows.forEach((item) => {
    if (!uniqueBySubCode.has(item.subCode)) {
      const displayLabel = item.subCode === "98" ? "98. N/A" : `${item.subCode} - ${item.label}`;
      uniqueBySubCode.set(item.subCode, {
        subCode: item.subCode,
        label: displayLabel,
      });
    }
  });

  return sortSubCodeOptions(Array.from(uniqueBySubCode.values()));
};

const SupplierProductCombinationNotesDialog = ({ supplierNotesDialog, setSupplierNotesDialog, onNotesChanged }) => {
  const { isOpen, supplierId, supplierName, supplierCountryId } = supplierNotesDialog;
  const [notes, setNotes] = useState([]);
  const [productData, setProductData] = useState([]);
  const [formValues, setFormValues] = useState(emptyForm);
  const [editingNoteId, setEditingNoteId] = useState(null);
  const [isEditorOpen, setIsEditorOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [notify, setNotify] = useState({ open: false, severity: "info", message: "" });

  const resetForm = useCallback(() => {
    setFormValues(emptyForm);
    setEditingNoteId(null);
  }, []);

  const handleClose = useCallback(() => {
    if (saving) {
      return;
    }

    setSupplierNotesDialog({ ...supplierNotesDialog, isOpen: false });
  }, [saving, setSupplierNotesDialog, supplierNotesDialog]);

  const handleOpenAddPopup = useCallback(() => {
    resetForm();
    setIsEditorOpen(true);
  }, [resetForm]);

  const handleOpenEditPopup = useCallback((note) => {
    setEditingNoteId(note.supplierProductCombinationNoteId);
    setFormValues({
      categoryCode: note.categoryCode || "",
      productSubCode: note.productSubCode || "",
      messageText: note.messageText || "",
    });
    setIsEditorOpen(true);
  }, []);

  const handleCloseEditorPopup = useCallback(() => {
    if (saving) {
      return;
    }

    setIsEditorOpen(false);
    resetForm();
  }, [resetForm, saving]);

  const loadProductTypes = useCallback(async () => {
    if (!isOpen) {
      return;
    }

    try {
      const response = await axios.get("/api/v1/productTypes");
      setProductData(response.data ?? []);
    } catch (error) {
      setProductData([]);
      setNotify({ open: true, severity: "error", message: "Unable to load product types." });
    }
  }, [isOpen]);

  const loadNotes = useCallback(async () => {
    if (!isOpen || !supplierId) {
      return;
    }

    setLoading(true);
    try {
      const response = await axios.get(`/api/v1/suppliers/${supplierId}/supplierProductCombinationNotes`);
      setNotes(response.data ?? []);
    } catch (error) {
      setNotify({ open: true, severity: "error", message: "Unable to load supplier notes." });
    } finally {
      setLoading(false);
    }
  }, [isOpen, supplierId]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    resetForm();
    loadProductTypes();
    loadNotes();
  }, [isOpen, loadNotes, loadProductTypes, resetForm]);

  const productTypeOptions = useMemo(() => {
    if (!supplierCountryId) {
      return [];
    }

    const uniqueByCode = new Map();
    productData
      .filter((item) => item.countryCode === supplierCountryId)
      .forEach((item) => {
        if (!uniqueByCode.has(item.categoryCode)) {
          uniqueByCode.set(item.categoryCode, item.productShortDescription || item.categoryCode);
        }
      });

    return Array.from(uniqueByCode.entries())
      .map(([code, label]) => ({ code: String(code), label: String(label || "") }))
      .sort((a, b) => a.code.localeCompare(b.code, undefined, { numeric: true, sensitivity: "base" }));
  }, [productData, supplierCountryId]);

  const productSubCodeOptions = useMemo(() => {
    return buildProductSubCodeOptions(productData, supplierCountryId, formValues.categoryCode);
  }, [formValues.categoryCode, productData, supplierCountryId]);

  const handleSubmit = async () => {
    if (!supplierId) {
      return;
    }

    const categoryCode = String(formValues.categoryCode || "").trim();
    const productSubCode = String(formValues.productSubCode || "").trim();
    const messageText = String(formValues.messageText || "").trim();

    if (!categoryCode || !productSubCode || !messageText) {
      setNotify({
        open: true,
        severity: "warning",
        message: "Category code, product sub code and message are required.",
      });
      return;
    }

    setSaving(true);
    try {
      const payload = {
        categoryCode,
        productSubCode,
        messageText,
      };

      if (editingNoteId) {
        await axios.put(`/api/v1/suppliers/${supplierId}/supplierProductCombinationNotes/${editingNoteId}`, payload);
        setNotify({ open: true, severity: "success", message: "Note updated." });
      } else {
        await axios.post(`/api/v1/suppliers/${supplierId}/supplierProductCombinationNotes`, payload);
        setNotify({ open: true, severity: "success", message: "Note added." });
      }

      setIsEditorOpen(false);
      resetForm();
      await loadNotes();
      onNotesChanged?.();
    } catch (error) {
      setNotify({ open: true, severity: "error", message: "Unable to save supplier note." });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (noteId) => {
    if (!supplierId) {
      return;
    }

    setSaving(true);
    try {
      await axios.delete(`/api/v1/suppliers/${supplierId}/supplierProductCombinationNotes/${noteId}`);
      setNotify({ open: true, severity: "success", message: "Note deleted." });
      if (editingNoteId === noteId) {
        resetForm();
      }
      await loadNotes();
      onNotesChanged?.();
    } catch (error) {
      setNotify({ open: true, severity: "error", message: "Unable to delete supplier note." });
    } finally {
      setSaving(false);
    }
  };

  const rows = useMemo(
    () =>
      notes.map((note) => ({
        ...note,
        id: note.supplierProductCombinationNoteId,
      })),
    [notes]
  );

  const columns = [
    { field: "categoryCode", headerName: "Category Code", width: 130 },
    { field: "productSubCode", headerName: "Product Sub Code", width: 140 },
    { field: "messageText", headerName: "Message", flex: 1, minWidth: 280 },
    {
      field: "actions",
      headerName: "Actions",
      width: 110,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      renderCell: (params) => (
        <Box>
          <Tooltip title="Edit note">
            <IconButton
              size="small"
              aria-label="Edit supplier note"
              onClick={() => handleOpenEditPopup(params.row)}
            >
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete note">
            <IconButton
              size="small"
              aria-label="Delete supplier note"
              onClick={() => handleDelete(params.row.supplierProductCombinationNoteId)}
            >
              <DeleteOutlineIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ];

  return (
    <React.Fragment>
      <Dialog open={Boolean(isOpen)} onClose={handleClose} fullWidth maxWidth="lg">
        <DialogTitle>Supplier Product Notes - {supplierName || "Supplier"}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Add one or more notes per combination of Category Code and Product Sub Code.
          </Typography>
          <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 2 }}>
            <Button variant="contained" onClick={handleOpenAddPopup} disabled={saving}>
              Add note
            </Button>
          </Box>
          <Box sx={{ height: 360, width: "100%" }}>
            <DataGrid
              rows={rows}
              columns={columns}
              loading={loading}
              disableSelectionOnClick
              density="compact"
              pageSize={10}
              rowsPerPageOptions={[10]}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} disabled={saving}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={isEditorOpen} onClose={handleCloseEditorPopup} fullWidth maxWidth="md">
        <DialogTitle>{editingNoteId ? "Edit Supplier Product Note" : "Add Supplier Product Note"}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap", mt: 1 }}>
            <TextField
              select
              label="Category Code"
              size="small"
              value={formValues.categoryCode}
              disabled={!supplierCountryId}
              helperText={!supplierCountryId ? "No CountryId found on supplier." : ""}
              onChange={(event) => {
                const nextCategoryCode = event.target.value;
                const nextSubCodeOptions = buildProductSubCodeOptions(productData, supplierCountryId, nextCategoryCode);
                const autoSelectedSubCode = nextSubCodeOptions.length === 1 ? nextSubCodeOptions[0].subCode : "";

                setFormValues((prev) => ({
                  ...prev,
                  categoryCode: nextCategoryCode,
                  productSubCode: autoSelectedSubCode,
                }));
              }}
              sx={{ minWidth: 200 }}
            >
              <MenuItem value="" disabled>
                Select category
              </MenuItem>
              {productTypeOptions.map((item) => (
                <MenuItem key={item.code} value={item.code}>
                  {item.code} - {item.label}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              select
              label="Product Sub Code"
              size="small"
              value={formValues.productSubCode}
              disabled={!formValues.categoryCode}
              helperText={!formValues.categoryCode ? "Select category code first." : ""}
              onChange={(event) =>
                setFormValues((prev) => ({
                  ...prev,
                  productSubCode: normalizeSubCode(event.target.value),
                }))
              }
              sx={{ minWidth: 200 }}
            >
              <MenuItem value="" disabled>
                Select sub code
              </MenuItem>
              {productSubCodeOptions.map((item) => (
                <MenuItem key={item.subCode} value={item.subCode}>
                  {item.label}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              label="Message"
              size="small"
              value={formValues.messageText}
              onChange={(event) => setFormValues((prev) => ({ ...prev, messageText: event.target.value }))}
              multiline
              minRows={3}
              maxRows={8}
              fullWidth
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseEditorPopup} disabled={saving}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleSubmit} disabled={saving}>
            {editingNoteId ? "Update note" : "Add note"}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={notify.open}
        autoHideDuration={4500}
        onClose={() => setNotify((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          severity={notify.severity}
          onClose={() => setNotify((prev) => ({ ...prev, open: false }))}
          sx={
            notify.severity === "warning"
              ? {
                  color: "#663c00 !important",
                  backgroundColor: "#fff4e5",
                  "& .MuiAlert-icon": {
                    color: "#663c00 !important",
                  },
                  "& .MuiAlert-message": {
                    color: "#663c00 !important",
                  },
                  "& .MuiAlert-action": {
                    color: "#663c00 !important",
                  },
                }
              : undefined
          }
        >
          {notify.message}
        </Alert>
      </Snackbar>
    </React.Fragment>
  );
};

export default SupplierProductCombinationNotesDialog;
